/**
 * reference: src/components/badges/components/BadgeProgress.jsx
 */
import ViewerUserPreferences from 'models/user/ViewerUserPreferences';
import LocaleMapper from 'utils/LocaleMapper';
import Personalizer from 'personalizer';
import { getNumberOfDaysBetween } from 'utils/DateUtils';
import { BADGE_UNIT_KEYS } from 'constants/BadgesConstants';
import i18next from 'i18next';

import './typedefs';
import { CHALLENGE_CATEGORY_ID } from './constants';

i18next.loadNamespaces(['units', 'userprofile', 'common', 'challengewidget']);

// The first decimal place of the progress value is rounded down when it's less than the target and rounded up when it meets the target
// > this prevents the user from assuming they've hit the target when their progress value is 10.25 with a target of 10.30
// > once the user hits their target, both values should use the same rounding method and round up
export const getProgressValue = (badgeProgressValue, badgeTargetValue, unit) => {
    let roundUp = false;

    if (badgeProgressValue >= badgeTargetValue) {
        roundUp = true;
        return getPersonalizedValue(badgeProgressValue, unit, roundUp);
    }

    return getPersonalizedValue(badgeProgressValue, unit, roundUp);
};

export const getBadgeTargetValue = (badgeTargetValue, unit) => {
    return getPersonalizedValue(badgeTargetValue, unit, true);
};

const getPersonalizedValue = (value, unit, roundUp) => {
    const unitKey = unit?.badgeUnitKey || unit;
    switch (unitKey) {
        case BADGE_UNIT_KEYS.distance:
            return Personalizer.roundDecimal(Personalizer.convertDistance(value), roundUp);

        case BADGE_UNIT_KEYS.distanceAlt:
            return Personalizer.roundDecimal(Personalizer.convertMetersFt(value), roundUp);

        case BADGE_UNIT_KEYS.miles:
            return Personalizer.roundDecimal(value * Personalizer.ConversionFactors.MetersToMiles, roundUp);

        case BADGE_UNIT_KEYS.seconds:
            return Personalizer.roundDecimal(value / 3600, roundUp);

        case BADGE_UNIT_KEYS.steps:
        case BADGE_UNIT_KEYS.kilocalories:
        case BADGE_UNIT_KEYS.likes:
        case BADGE_UNIT_KEYS.weeks:
            return Personalizer.formatWholeNumber(value);

        case BADGE_UNIT_KEYS.activities:
        case BADGE_UNIT_KEYS.days:
        default:
            return value;
    }
};

export const getBadgeLocalizedUnit = (unit) => {
    switch (unit?.badgeUnitKey || unit) {
        case BADGE_UNIT_KEYS.distance:
            return i18next.t(`units:${Personalizer.getDistanceUnitKey()}`);
        case BADGE_UNIT_KEYS.distanceAlt:
            return i18next.t(`units:${Personalizer.getMetersFtUnitKey()}`);
        case BADGE_UNIT_KEYS.miles:
            return i18next.t('units:distance_unit_statute');
        case BADGE_UNIT_KEYS.activities:
            return i18next.t('userprofile:activities').toLocaleLowerCase();
        case BADGE_UNIT_KEYS.days:
            return i18next.t('common:label_days').toLocaleLowerCase();
        case BADGE_UNIT_KEYS.steps:
            return i18next.t('common:steps').toLocaleLowerCase();
        case BADGE_UNIT_KEYS.kilocalories:
            return i18next.t('units:progress_active_calories').toLocaleLowerCase();
        case BADGE_UNIT_KEYS.weeks:
            return i18next.t('units:progress_goals_met').toLocaleLowerCase();
        case BADGE_UNIT_KEYS.likes:
            return i18next.t('units:progress_likes').toLocaleLowerCase();
        case BADGE_UNIT_KEYS.seconds:
            return i18next.t('units:duration_hours_lowercase').toLocaleLowerCase();
        default:
            return '';
    }
};

export const getUnit = (unitId, units) => {
    return units.find((unit) => unit.badgeUnitId === unitId);
};

export const getBadgeChallengeUnit = (badgeUnitId) => {
    if (badgeUnitId === 0) {
        badgeUnitId = 1;
    }
    const key = Object.keys(BADGE_UNIT_KEYS)[badgeUnitId - 1];
    return BADGE_UNIT_KEYS[key] || null;
};

/**
 * Returns formatted approximate value.
 * @param {number} approximateValue
 */
function formatApproximateValue(approximateValue) {
    const preferredLocale = ViewerUserPreferences.get('preferredLocale');
    const locale = LocaleMapper.getFullLocale(preferredLocale);
    return new Intl.NumberFormat(locale, { notation: 'compact' }).format(approximateValue);
}

/**
 *
 * @param {TBadgeChallengeDTO} badgeChallenge
 * @param {boolean} withoutApproximate
 */
export const formatBadgeTargetValue = (badgeChallenge, withoutApproximate = false) => {
    if (!badgeChallenge) {
        return '';
    }

    const unit = getBadgeChallengeUnit(badgeChallenge.badgeUnitId);
    const targetValue = getBadgeTargetValue(badgeChallenge.badgeTargetValue, unit);
    const localizedUnit = getBadgeLocalizedUnit(unit);

    if (withoutApproximate) {
        return `${targetValue} ${localizedUnit}`;
    }

    const formattedValue = formatApproximateValue(badgeChallenge.approximateValue);
    let approximateText = '';

    switch (badgeChallenge.challengeCategoryId) {
        case CHALLENGE_CATEGORY_ID.VIRTUAL_HIKE:
            approximateText = i18next.t('challengewidget:adventure_approximate_step_count', { 0: formattedValue });
            break;
        case CHALLENGE_CATEGORY_ID.VIRTUAL_CLIMB:
            approximateText = i18next.t('challengewidget:adventure_approximate_floor_count', { 0: formattedValue });
            break;
        default:
            break;
    }

    if (!approximateText) {
        return `${targetValue} ${localizedUnit}`;
    }

    return `${targetValue} ${localizedUnit} (${approximateText})`;
};

/**
 *
 * @param {function} t
 * @param {TBadgeChallengeDTOProgression} badgeChallenge
 * @param {boolean} withoutPercentage
 */
const formatTimeBasedChallengeProgressionText = (t, badgeChallenge, withoutPercentage) => {
    if (!badgeChallenge) {
        return '';
    }

    const { badgeProgressValue, badgeTargetValue } = badgeChallenge;
    const progressPercent = Math.floor((badgeProgressValue * 100) / badgeTargetValue);
    const progressValue = Personalizer.personalizeStandardTimeDuration(badgeProgressValue, t);
    const targetValue = Personalizer.personalizeStandardTimeDuration(badgeTargetValue, t);

    if (withoutPercentage) {
        return t('challengewidget:challenge_progress', {
            0: progressValue,
            1: targetValue
        });
    }

    return t('challengewidget:challenge_progress', {
        0: progressValue,
        1: `${targetValue} • ${progressPercent}%`
    });
};

/**
 *
 * @param {function} t
 * @param {TBadgeChallengeDTO | TBadgeChallengeDTOProgression} badgeChallenge
 * @param {boolean} withoutPercentage
 */
export const formatProgressionText = (t, badgeChallenge, withoutPercentage = false) => {
    if (!badgeChallenge) {
        return '';
    }

    const { badgeProgressValue, badgeTargetValue } = badgeChallenge;
    const progressPercent = Math.floor((badgeProgressValue * 100) / badgeTargetValue);
    const unit = getBadgeChallengeUnit(badgeChallenge.badgeUnitId);

    if (BADGE_UNIT_KEYS.seconds === (unit?.badgeUnitKey || unit)) {
        return formatTimeBasedChallengeProgressionText(t, badgeChallenge, withoutPercentage);
    }

    const progressValue = getProgressValue(badgeProgressValue, badgeTargetValue, unit);
    const targetValue = getBadgeTargetValue(badgeTargetValue, unit);
    const localizedUnit = getBadgeLocalizedUnit(unit);

    if (withoutPercentage) {
        return t('challengewidget:challenge_progress', {
            0: progressValue,
            1: `${targetValue} ${localizedUnit}`
        });
    }

    return t('challengewidget:challenge_progress', {
        0: progressValue,
        1: `${targetValue} ${localizedUnit} • ${progressPercent}%`
    });
};

/**
 *
 * @param {TBadgeChallengeDTO} badgeChallenge
 */
export const formatCompleteDurationText = (badgeChallenge) => {
    const startDate = new Date(badgeChallenge.joinDateLocal);
    const earnedDate = new Date(badgeChallenge.badgeEarnedDate);

    /**
     * time diff in calendar days.
     * ex: Sep 1 start, Sep 2 end, we consider it done in 2 days.
     */
    const daysDiff = getNumberOfDaysBetween(startDate, earnedDate) + 1;

    const daysDiffText = Personalizer.formatWholeNumber(daysDiff);

    if (daysDiff > 1) {
        return i18next.t('challengewidget:expedition_complete_in_days', { 0: daysDiffText });
    } else {
        return i18next.t('challengewidget:expedition_complete_in_1_day');
    }
};
